import React from 'react';
import classnames from 'classnames/bind';
import { Skeleton } from '@mantine/core';
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router-dom';

import Application from 'types/application';

import { useAccount } from 'hooks/api/useAccount';
import { useAPI } from 'hooks/api/useAPI';
import { useApplications } from 'hooks/api/useApplications';
import { useCurrentApplication } from 'application/hooks/useCurrentApplication';
import { useCurrentOrganization } from 'hooks/api/useCurrentOrganization';
import { useCurrentPlan } from 'organizations/hooks/useCurrentPlan';
import { useGroupedApplications } from 'application/hooks/useGroupedApplications';
import { useOrganizations } from 'hooks/api/useOrganizations';

import {
  ContextMenu,
  ContextMenuGroup,
  ContextMenuLabel,
  ContextMenuOption,
  ContextMenuOptionGroup,
} from 'components/ContextMenu/ContextMenu';
import LogoIcon from 'images/lumeo-logo-icon.svg?react';
import { Icon } from 'components/Icon/IconDeprecated';
import { Pill } from 'components/Pill/Pill';
import { Text } from 'components/Text/Text';

import styles from './ApplicationSelect.module.scss';

const c = classnames.bind(styles);

function formatTrialEndDate(trialEndDate: string) {
  return DateTime.fromISO(trialEndDate).toLocaleString({
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function ApplicationSelect() {
  const navigate = useNavigate();

  const { applicationID } = useAPI();

  const { data: currentApplication, isInitialLoading: isLoadingApplication } =
    useCurrentApplication();
  const { data: currentOrganization, isInitialLoading: isLoadingOrganization } =
    useCurrentOrganization();
  const { data: currentPlan, isInitialLoading: isLoadingPlan } =
    useCurrentPlan();
  const { data: applications } = useApplications();
  const { data: organizations } = useOrganizations();
  const { data: account } = useAccount();

  const groupedApplications = useGroupedApplications();

  const [query, setQuery] = React.useState('');
  const filteredApplicationGroups = React.useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    if (!normalizedQuery) {
      return groupedApplications;
    }

    return groupedApplications
      .map((group) => ({
        ...group,
        applications: group.name.toLowerCase().includes(normalizedQuery)
          ? group.applications
          : group.applications.filter((application) =>
              application.name.toLowerCase().includes(normalizedQuery)
            ),
      }))
      .filter((group) => group.applications.length > 0);
  }, [groupedApplications, query]);

  if (!applications || !organizations || !account) {
    return null;
  }

  function handleApplicationSelect(application: Application) {
    if (!account || application.id === currentApplication?.id) {
      return;
    }

    navigate(`/applications/${application.id}/overview`);
  }

  function handleOpenChange() {
    // ContextMenu focuses the search field on open; just reset the filter.
    setQuery('');
  }

  const isSuspended = Boolean(currentOrganization?.is_suspended);
  const isTrialEndedSuspension =
    isSuspended && currentOrganization?.suspension_reason === 'trial_end';

  return (
    <>
      <div className={c('wrap')}>
        <ContextMenu
          trigger={
            <button className={c('trigger')} aria-label="Select workspace">
              <div className={c('workspace-picture')}>
                <Skeleton radius="xl" visible={isLoadingApplication}>
                  <LogoIcon className={c('workspace-picture-img')} />
                </Skeleton>
              </div>

              {applicationID ? (
                <div>
                  <Skeleton
                    visible={isLoadingOrganization}
                    width="50%"
                    height="12px"
                    mb="2px"
                  >
                    <Text size="small">
                      {currentOrganization?.name ?? 'Organization'}
                    </Text>
                  </Skeleton>

                  <Skeleton visible={isLoadingApplication}>
                    <Text className={c('application-name')}>
                      {currentApplication?.name ?? 'Workspace'}
                    </Text>
                  </Skeleton>
                </div>
              ) : (
                <Text className={c('application-name', 'default')}>
                  Select workspace...
                </Text>
              )}

              <div className={c('icon-wrap')}>
                <Icon
                  name="unfold-vertical"
                  className={c('indicators')}
                  size="medium"
                />
              </div>
            </button>
          }
          align="start"
          onOpenChange={handleOpenChange}
          header={
            <div className={c('search')}>
              <Icon name="search" size="small" />
              <input
                type="text"
                value={query}
                placeholder="Find workspace..."
                aria-label="Find workspace"
                autoComplete="off"
                spellCheck={false}
                onChange={(event) => setQuery(event.target.value)}
              />
            </div>
          }
        >
          <ContextMenuOptionGroup>
            {filteredApplicationGroups.map(({ id, name, applications }) => (
              <ContextMenuGroup key={id}>
                <ContextMenuLabel>{name}</ContextMenuLabel>
                {applications.map((application) => (
                  <ContextMenuOption
                    value={application.id}
                    key={application.id}
                    onClick={() => handleApplicationSelect(application)}
                  >
                    {application.name}
                  </ContextMenuOption>
                ))}
              </ContextMenuGroup>
            ))}
          </ContextMenuOptionGroup>

          {filteredApplicationGroups.length === 0 && (
            <ContextMenuLabel>No matching workspaces</ContextMenuLabel>
          )}
        </ContextMenu>

        <Skeleton visible={isLoadingPlan || isLoadingOrganization} radius="xl">
          <div className={c('plan-pills')}>
            <Pill intent="default">{currentPlan?.name ?? 'No Plan'}</Pill>

            {isSuspended && !isTrialEndedSuspension && (
              <Pill intent="danger">Suspended</Pill>
            )}

            {isTrialEndedSuspension ? (
              <Pill intent="danger">Trial ended</Pill>
            ) : currentOrganization?.trial_end_date ? (
              <Pill intent="warning">
                Trial ends{' '}
                {formatTrialEndDate(currentOrganization.trial_end_date)}
              </Pill>
            ) : null}
          </div>
        </Skeleton>
      </div>
    </>
  );
}
